import { useEffect, useState } from "react";

import SearchableSelect from "@/components/SearchableSelect";

export type FilterField =
  | { key: string; label: string; type: "text"; placeholder?: string }
  | {
      key: string;
      label: string;
      type: "select";
      options: { label: string; value: string }[];
      default?: string;
      disabled?: boolean;
    }
  | {
      label: string;
      type: "date-range";
      keyFrom: string;
      keyTo: string;
      minFrom?: string;
      defaultFrom?: string;
    };

type FilterBarProps = {
  fields: FilterField[];
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
};

const INPUT =
  "h-9 w-full rounded-full border-2 border-ink/20 bg-white px-4 font-sans text-sm font-light text-ink placeholder:text-ink/40 focus:border-steel focus:outline-none focus:ring-2 focus:ring-steel/30";

function allKeys(fields: FilterField[]): string[] {
  return fields.flatMap((f) =>
    f.type === "date-range" ? [f.keyFrom, f.keyTo] : [f.key],
  );
}

function emptyRecord(keys: string[]): Record<string, string> {
  return Object.fromEntries(keys.map((k) => [k, ""]));
}

// clearAll() resets to each field's own "cleared" state, not blank for
// every key — a date-range's keyFrom clears back to its default (e.g.
// today) rather than an unbounded lower bound.
function clearedRecord(fields: FilterField[]): Record<string, string> {
  const result: Record<string, string> = {};
  for (const field of fields) {
    if (field.type === "date-range") {
      result[field.keyFrom] = field.defaultFrom ?? "";
      result[field.keyTo] = "";
    } else {
      result[field.key] = field.type === "select" ? (field.default ?? "") : "";
    }
  }
  return result;
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-1 block font-sans text-xs font-medium uppercase tracking-wide text-ink/50">
      {children}
    </span>
  );
}

export default function FilterBar({
  fields,
  values,
  onChange,
}: FilterBarProps) {
  const keys = allKeys(fields);
  const [local, setLocal] = useState<Record<string, string>>({
    ...emptyRecord(keys),
    ...values,
  });
  const valuesJson = JSON.stringify(values);

  // biome-ignore lint/correctness/useExhaustiveDependencies: valuesJson is the stable dep
  useEffect(() => {
    setLocal({ ...emptyRecord(keys), ...values });
  }, [valuesJson]);

  function patch(key: string, value: string) {
    setLocal((prev) => ({ ...prev, [key]: value }));
  }

  function submit() {
    onChange(local);
  }

  function clearAll() {
    const cleared = clearedRecord(fields);
    setLocal(cleared);
    onChange(cleared);
  }

  return (
    <form
      className="mb-6"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {fields.map((field) => {
          if (field.type === "text") {
            return (
              <div key={field.key}>
                <Label>{field.label}</Label>
                <input
                  type="text"
                  className={INPUT}
                  placeholder={field.placeholder}
                  value={local[field.key] ?? ""}
                  onChange={(e) => patch(field.key, e.target.value)}
                />
              </div>
            );
          }

          if (field.type === "select") {
            return (
              <div key={field.key}>
                <Label>{field.label}</Label>
                <SearchableSelect
                  className={INPUT}
                  value={local[field.key] ?? ""}
                  onChange={(value) => patch(field.key, value)}
                  options={field.options}
                  disabled={field.disabled}
                />
              </div>
            );
          }

          if (field.type === "date-range") {
            return (
              <div
                key={`${field.keyFrom}-${field.keyTo}`}
                className="sm:col-span-2"
              >
                <Label>{field.label}</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    className={INPUT}
                    min={field.minFrom}
                    value={local[field.keyFrom] ?? ""}
                    onChange={(e) => patch(field.keyFrom, e.target.value)}
                  />
                  <span className="shrink-0 font-sans text-xs text-ink/30">
                    –
                  </span>
                  <input
                    type="date"
                    className={INPUT}
                    value={local[field.keyTo] ?? ""}
                    onChange={(e) => patch(field.keyTo, e.target.value)}
                  />
                </div>
              </div>
            );
          }

          return null;
        })}
      </div>

      <div className="mt-4 flex gap-3">
        <button
          type="submit"
          className="inline-flex h-9 items-center rounded-full bg-steel px-5 font-sans text-sm font-semibold text-white transition-colors hover:bg-steel/90"
        >
          Buscar
        </button>
        <button
          type="button"
          onClick={clearAll}
          className="font-sans text-xs text-ink/50 underline underline-offset-2 transition-colors hover:text-ink self-center"
        >
          Limpiar
        </button>
      </div>
    </form>
  );
}
